/**
 * MARKETPLACE PAGE
 *
 * Community marketplace for buying, trading, and discovering nail art items and creations.
 *
 * Five Marketplace Tabs:
 * 1. Destash - Collection items users are selling
 * 2. Trade - Items available for swaps/trades
 * 3. Press-Ons - Custom press-on nail sets for sale
 * 4. Jewelry - Nail polish bottle jewelry and accessories
 * 5. Other - Miscellaneous nail art creations
 *
 * Data Sources:
 * - Destash/Trade: Queries users' collection items where isForDestash=true or isOpenToSwaps=true
 * - Press-Ons/Jewelry/Other: Queries from the 'creations' collection by category
 *
 * Key Features:
 * - Watch/unwatch items and creations (bookmarking for later)
 * - Filter by item type (polish, plates, tools, etc.)
 * - Search by name or brand
 * - Seller filtering via URL param (?seller=username)
 * - Click item to open detail modal with seller info
 * - Contact seller via messaging system
 * - Navigation between items using prev/next in modal
 *
 * Watching System:
 * - Users can "watch" items/creations (like a wishlist)
 * - Watched items stored in user's watchingItems/watchingCreations arrays
 * - Watch state syncs between cards and detail modals
 * - View all watched items from /marketplace/favorites
 *
 * Technical Notes:
 * - Uses Firestore collectionGroup queries to fetch items across all users
 * - Enriches items with seller username/info for display
 * - Handles pluralization for item type labels
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { query, where, collectionGroup, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { Search, RefreshCw, Store, Repeat, Sparkles, Gem, Package, X, User, Heart, Star, ShoppingBag } from 'lucide-react';
import MarketplaceItemCard from '../components/marketplace/MarketplaceItemCard';
import MarketplaceCreationCard from '../components/marketplace/MarketplaceCreationCard';
import MarketplaceItemDetailModal from '../components/marketplace/MarketplaceItemDetailModal';
import MarketplaceCreationDetailModal from '../components/marketplace/MarketplaceCreationDetailModal';
import { PageHeader } from '../components/common';
import { CategoryColors } from '../constants/categoryColors';
import { groupCountriesByRegion, getCountryInfo, type CountryRegion } from '../constants/countries';
import type { CollectionItem } from '../types';
import type { Creation } from '../types/creationTypes';

// Extended type to include seller info
interface MarketplaceItem extends CollectionItem {
  sellerId: string;
  sellerUsername?: string;
}

interface MarketplaceCreation extends Creation {
  sellerId: string;
  sellerUsername?: string;
}

type TabType = 'destash' | 'trade' | 'pressons' | 'jewelry' | 'other';

const TABS: { id: TabType; label: string; icon: React.ReactNode }[] = [
  { id: 'destash', label: 'Destash', icon: <Store className="icon-sm text-current" /> },
  { id: 'trade', label: 'Trade', icon: <Repeat className="icon-sm text-current" /> },
  { id: 'pressons', label: 'Press-Ons', icon: <Sparkles className="icon-sm text-current" /> },
  { id: 'jewelry', label: 'Jewelry', icon: <Gem className="icon-sm text-current" /> },
  { id: 'other', label: 'Other', icon: <Package className="icon-sm text-current" /> },
];

// Item type categories (alphabetized)
const ITEM_CATEGORIES = [
  { key: 'all', label: 'All Types' },
  { key: 'acrylic', label: 'Acrylics' },
  { key: 'transfer', label: 'Decals' },
  { key: 'embellishment', label: 'Embellishments' },
  { key: 'plate', label: 'Plates' },
  { key: 'polish', label: 'Polishes' },
  { key: 'powder', label: 'Powders' },
  { key: 'tool', label: 'Tools' },
];

// Subcategories by item type (alphabetized)
const SUBCATEGORIES: Record<string, { key: string; label: string }[]> = {
  polish: [
    { key: 'all', label: 'All Polishes' },
    { key: 'Base Coat', label: 'Base Coat' },
    { key: 'Builder Gel', label: 'Builder Gel' },
    { key: 'Dip Powder', label: 'Dip Powder' },
    { key: 'Gel Polish', label: 'Gel Polish' },
    { key: 'Nail Lacquer', label: 'Nail Lacquer' },
    { key: 'Poly Gel', label: 'Poly Gel' },
    { key: 'Stamping Polish', label: 'Stamping Polish' },
    { key: 'Top Coat', label: 'Top Coat' },
    { key: 'Treatment', label: 'Treatment' },
  ],
  embellishment: [
    { key: 'all', label: 'All Embellishments' },
    { key: 'Chains', label: 'Chains' },
    { key: 'Charms', label: 'Charms' },
    { key: 'Crystals', label: 'Crystals' },
    { key: 'Dried Flowers', label: 'Dried Flowers' },
    { key: 'Foil', label: 'Foil' },
    { key: 'Glitter', label: 'Glitter' },
    { key: 'Pearls', label: 'Pearls' },
    { key: 'Rhinestones', label: 'Rhinestones' },
    { key: 'Sequins', label: 'Sequins' },
    { key: 'Studs', label: 'Studs' },
    { key: 'Wire', label: 'Wire' },
    { key: 'Other', label: 'Other' },
  ],
  transfer: [
    { key: 'all', label: 'All Decals' },
    { key: 'Foil', label: 'Foil' },
    { key: 'Nail Wrap', label: 'Nail Wrap' },
    { key: 'Sticker', label: 'Sticker' },
    { key: 'Waterslide Decal', label: 'Waterslide Decal' },
  ],
};

// Condition options
const CONDITION_OPTIONS = [
  { key: 'all', label: 'Any Condition' },
  { key: 'New', label: 'New' },
  { key: 'Swatched Only', label: 'Swatched Only' },
  { key: 'Used - Light Use', label: 'Used - Light Use' },
  { key: 'Used - Moderate', label: 'Used - Moderate' },
];

// Price range options
const PRICE_RANGE_OPTIONS = [
  { key: 'all', label: 'Any Price' },
  { key: '0-10', label: 'Under $10' },
  { key: '10-25', label: '$10 - $25' },
  { key: '25-50', label: '$25 - $50' },
  { key: '50+', label: '$50+' },
];

// Sort options
const SORT_OPTIONS = [
  { key: 'recent', label: 'Recently Listed' },
  { key: 'price-low', label: 'Price: Low to High' },
  { key: 'price-high', label: 'Price: High to Low' },
  { key: 'name-az', label: 'Name: A-Z' },
];

// Ships-To filter: country dropdown grouped by region. "all" disables the filter.
// Region ordering matches groupCountriesByRegion() insertion order.
const SHIPS_TO_COUNTRY_GROUPS = groupCountriesByRegion();
const SHIPS_TO_REGION_ORDER: CountryRegion[] = [
  'North America', 'Europe', 'Oceania', 'Asia', 'Latin America', 'Africa', 'Middle East',
];

/**
 * Legacy fuzzy-match keywords by ISO code. Used only when an item has
 * neither `destashShipsToCountries` nor `destashShipsToWorldwide` set —
 * we fall back to fuzzy-matching against the seller's free-text
 * `shipsTo` string so pre-migration listings still surface in
 * country-filtered results.
 */
const LEGACY_SHIPS_TO_KEYWORDS: Record<string, string[]> = {
  US: ['us', 'usa', 'united states', 'domestic', 'conus'],
  CA: ['canada', 'canadian'],
  GB: ['uk', 'united kingdom', 'britain', 'england'],
  AU: ['australia', 'australian', 'aus'],
  NZ: ['new zealand', 'nz'],
  DE: ['germany', 'deutschland'],
  FR: ['france'],
  JP: ['japan'],
  RO: ['romania', 'romanian'],
  TR: ['turkey', 'türkiye', 'turkish'],
};
const LEGACY_WORLDWIDE_KEYWORDS = ['international', 'worldwide', 'global', 'anywhere', 'world'];

// Helper function for proper pluralization (kept for future use)
// const pluralize = (word: string): string => {
//   const irregulars: Record<string, string> = {
//     'polish': 'Polishes',
//     'plate': 'Plates',
//     'tool': 'Tools',
//     'embellishment': 'Embellishments',
//     'transfer': 'Transfers',
//     'powder': 'Powders',
//     'acrylic': 'Acrylics',
//   };
//   const lower = word.toLowerCase();
//   if (irregulars[lower]) {
//     return irregulars[lower];
//   }
//   // Default pluralization rules
//   if (lower.endsWith('s') || lower.endsWith('x') || lower.endsWith('z') ||
//       lower.endsWith('ch') || lower.endsWith('sh')) {
//     return word.charAt(0).toUpperCase() + word.slice(1) + 'es';
//   }
//   return word.charAt(0).toUpperCase() + word.slice(1) + 's';
// };

const Marketplace: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('destash');
  
  // Items state (for destash/trade tabs)
  const [destashItems, setDestashItems] = useState<MarketplaceItem[]>([]);
  const [tradeItems, setTradeItems] = useState<MarketplaceItem[]>([]);
  
  // Creations state (for pressons/jewelry/other tabs)
  const [creations, setCreations] = useState<MarketplaceCreation[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [itemTypeFilter, setItemTypeFilter] = useState<string>('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState<string>('all');
  const [conditionFilter, setConditionFilter] = useState<string>('all');
  const [swapsOnlyFilter, setSwapsOnlyFilter] = useState<boolean>(false);
  const [priceRangeFilter, setPriceRangeFilter] = useState<string>('all');
  // Ships-to filter holds an ISO alpha-2 country code or 'all' (disabled).
  // Defaults to the viewer's shipsFromCountry once loaded (or 'US' if unknown).
  const [shipsToFilter, setShipsToFilter] = useState<string>('all');
  // Whether the ships-to filter has been auto-initialized from the viewer's
  // profile. We only auto-set once so we don't clobber user changes.
  const [shipsToFilterInitialized, setShipsToFilterInitialized] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>('recent');
  
  // Seller shipping info cache (sellerId -> shipsTo string)
  const [sellerShippingInfo, setSellerShippingInfo] = useState<Record<string, string | null>>({});
  
  // Detail modal state
  const [selectedItem, setSelectedItem] = useState<MarketplaceItem | null>(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(-1);
  const [selectedCreation, setSelectedCreation] = useState<MarketplaceCreation | null>(null);
  const [selectedCreationIndex, setSelectedCreationIndex] = useState<number>(-1);
  
  // Seller filter (from URL param ?seller=username)
  const [sellerFilter, setSellerFilter] = useState<{
    username: string;
    displayName: string;
    userId: string;
  } | null>(null);
  
  // Watching state - tracks which items/creations are being watched
  const [watchingItems, setWatchingItems] = useState<Record<string, boolean>>({});
  const [watchingCreations, setWatchingCreations] = useState<Record<string, boolean>>({});
  
  // Handlers for watch state changes
  const handleItemWatchChange = (itemId: string, sellerId: string, isWatching: boolean) => {
    setWatchingItems(prev => ({ ...prev, [`${itemId}-${sellerId}`]: isWatching }));
  };
  
  const handleCreationWatchChange = (creationId: string, creatorId: string, isWatching: boolean) => {
    setWatchingCreations(prev => ({ ...prev, [`${creationId}-${creatorId}`]: isWatching }));
  };

  // Handle edit listing - navigate to collection with item ID to edit
  const handleEditListing = (item: MarketplaceItem) => {
    // Navigate to collection page with state to open the item for editing
    navigate('/collection', { 
      state: { 
        editItemId: item.id,
        editItemType: item.itemType 
      } 
    });
  };

  // Get current user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUserId(user?.uid || null);
    });
    return () => unsubscribe();
  }, []);

  // Default the ships-to filter to the viewer's own shipsFromCountry (or 'US')
  // so most users see the listings that actually ship to them first thing.
  // Only runs once per session — if the user manually changes the filter,
  // subsequent profile-fetches must not overwrite their choice.
  useEffect(() => {
    if (shipsToFilterInitialized) return;

    const initializeShipsToFilter = async () => {
      // Signed-out viewer: fall back to 'US' immediately (US-heavy user base)
      // and mark initialized so we don't re-run after a login mid-session.
      if (!currentUserId) {
        setShipsToFilter('US');
        setShipsToFilterInitialized(true);
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUserId));
        // Mirror the existing seller-lookup pattern: check both the flat
        // `shipsFromCountry` field and the nested `profile.shipsFromCountry`
        // since older profiles kept these under a `profile` sub-object.
        const data = userDoc.exists() ? userDoc.data() : null;
        const viewerCountry =
          (data?.shipsFromCountry as string | undefined) ||
          (data?.profile?.shipsFromCountry as string | undefined) ||
          'US';
        setShipsToFilter(viewerCountry);
      } catch (err) {
        console.error('Error loading viewer shipsFromCountry:', err);
        setShipsToFilter('US');
      } finally {
        setShipsToFilterInitialized(true);
      }
    };

    initializeShipsToFilter();
  }, [currentUserId, shipsToFilterInitialized]);

  // Handle seller filter from URL param
  useEffect(() => {
    const lookupSeller = async () => {
      const sellerUsername = searchParams.get('seller');
      
      if (!sellerUsername) {
        setSellerFilter(null);
        return;
      }
      
      try {
        // Look up user by username
        const usernameDoc = await getDoc(doc(db, 'usernames', sellerUsername.toLowerCase()));
        
        if (usernameDoc.exists()) {
          const userId = usernameDoc.data()?.userId || usernameDoc.data()?.uid;
          
          if (userId) {
            // Get user profile for display name
            const userDoc = await getDoc(doc(db, 'users', userId));
            const userData = userDoc.data();
            
            setSellerFilter({
              username: sellerUsername,
              displayName: userData?.displayName || userData?.profile?.displayName || sellerUsername,
              userId: userId
            });
          }
        }
      } catch (err) {
        console.error('Error looking up seller:', err);
      }
    };
    
    lookupSeller();
  }, [searchParams]);

  // Clear seller filter
  const clearSellerFilter = () => {
    setSellerFilter(null);
    setSearchParams({});
  };

  // Fetch all marketplace data
  const fetchMarketplaceData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch destash items (exclude deleted/destashed items)
      const destashQuery = query(
        collectionGroup(db, 'items'),
        where('isForDestash', '==', true)
      );
      const destashSnapshot = await getDocs(destashQuery);

      const destashList: MarketplaceItem[] = [];
      destashSnapshot.forEach((doc) => {
        const data = doc.data() as CollectionItem & { hiddenByBan?: boolean };
        // Skip deleted, already-destashed, or ban-hidden items. `hiddenByBan`
        // is set by the onModerationStateChange trigger when the seller is
        // banned so their listings disappear from every other user's grid.
        if (data.isDeleted || data.isDestashed || data.hiddenByBan === true) return;
        const pathParts = doc.ref.path.split('/');
        const sellerId = pathParts[1];
        destashList.push({ ...data, id: doc.id, sellerId });
      });
      setDestashItems(destashList);

      // Fetch trade items (exclude deleted/destashed items)
      const tradeQuery = query(
        collectionGroup(db, 'items'),
        where('isOpenToSwaps', '==', true)
      );
      const tradeSnapshot = await getDocs(tradeQuery);

      const tradeList: MarketplaceItem[] = [];
      tradeSnapshot.forEach((doc) => {
        const data = doc.data() as CollectionItem & { hiddenByBan?: boolean };
        // Skip deleted, already-destashed, or ban-hidden items.
        if (data.isDeleted || data.isDestashed || data.hiddenByBan === true) return;
        const pathParts = doc.ref.path.split('/');
        const sellerId = pathParts[1];
        tradeList.push({ ...data, id: doc.id, sellerId });
      });
      setTradeItems(tradeList);

      // Fetch marketplace creations
      const creationsQuery = query(
        collectionGroup(db, 'creations'),
        where('listedOnMarketplace', '==', true)
      );
      const creationsSnapshot = await getDocs(creationsQuery);
      
      const creationsList: MarketplaceCreation[] = [];
      creationsSnapshot.forEach((doc) => {
        const data = doc.data() as Creation & { hiddenByBan?: boolean; isDeleted?: boolean };
        // Skip ban-hidden or user-deleted creations. Deleted-check is
        // defensive: the old code didn't check it here, but if a creation
        // is soft-deleted it shouldn't remain on the marketplace either.
        if (data.hiddenByBan === true || data.isDeleted === true) return;
        const pathParts = doc.ref.path.split('/');
        const sellerId = pathParts[1];
        creationsList.push({ ...data, id: doc.id, sellerId });
      });
      setCreations(creationsList);
      
    } catch (err: any) {
      console.error('Error fetching marketplace data:', err);
      
      if (err.message?.includes('index')) {
        setError('Database index required. Check the browser console for a link to create it.');
      } else {
        setError('Failed to load marketplace. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchMarketplaceData();
  }, [currentUserId]);

  // Fetch seller shipping info for all unique sellers (for Ships To filter)
  useEffect(() => {
    const fetchSellerShippingInfo = async () => {
      // Get unique seller IDs from all items
      const allItems = [...destashItems, ...tradeItems];
      const uniqueSellerIds = [...new Set(allItems.map(item => item.sellerId))];
      
      // Filter out sellers we already have info for
      const newSellerIds = uniqueSellerIds.filter(id => !(id in sellerShippingInfo));
      
      if (newSellerIds.length === 0) return;
      
      // Fetch shipping info for new sellers
      const newShippingInfo: Record<string, string | null> = {};
      
      await Promise.all(newSellerIds.map(async (sellerId) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', sellerId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            newShippingInfo[sellerId] = userData.shipsTo || userData.profile?.shipsTo || null;
          } else {
            newShippingInfo[sellerId] = null;
          }
        } catch (err) {
          console.error(`Error fetching shipping info for seller ${sellerId}:`, err);
          newShippingInfo[sellerId] = null;
        }
      }));
      
      setSellerShippingInfo(prev => ({ ...prev, ...newShippingInfo }));
    };
    
    if (destashItems.length > 0 || tradeItems.length > 0) {
      fetchSellerShippingInfo();
    }
  }, [destashItems, tradeItems]);

  // Get filtered items based on active tab
  const getFilteredContent = () => {
    let items: MarketplaceItem[] = [];
    let filteredCreations: MarketplaceCreation[] = [];
    
    switch (activeTab) {
      case 'destash':
        items = destashItems;
        break;
      case 'trade':
        items = tradeItems;
        break;
      case 'pressons':
        filteredCreations = creations.filter(c => c.marketplaceCategory === 'pressons');
        break;
      case 'jewelry':
        filteredCreations = creations.filter(c => c.marketplaceCategory === 'jewelry');
        break;
      case 'other':
        filteredCreations = creations.filter(c => c.marketplaceCategory === 'other');
        break;
    }
    
    // Apply seller filter (from URL param)
    if (sellerFilter) {
      if (activeTab === 'destash' || activeTab === 'trade') {
        items = items.filter(item => item.sellerId === sellerFilter.userId);
      } else {
        filteredCreations = filteredCreations.filter(c => c.sellerId === sellerFilter.userId);
      }
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      
      if (activeTab === 'destash' || activeTab === 'trade') {
        items = items.filter(item => 
          item.name?.toLowerCase().includes(term) ||
          item.brand?.toLowerCase().includes(term) ||
          item.colorDescription?.toLowerCase().includes(term)
        );
      } else {
        filteredCreations = filteredCreations.filter(creation =>
          creation.title?.toLowerCase().includes(term) ||
          creation.description?.toLowerCase().includes(term)
        );
      }
    }
    
    // Apply item type/category filter (only for items, not creations)
    if (itemTypeFilter !== 'all' && (activeTab === 'destash' || activeTab === 'trade')) {
      items = items.filter(item => item.itemType === itemTypeFilter);
    }
    
    // Apply subcategory filter (only for items with subcategories)
    if (subcategoryFilter !== 'all' && (activeTab === 'destash' || activeTab === 'trade')) {
      if (itemTypeFilter === 'polish') {
        items = items.filter(item => item.category === subcategoryFilter);
      } else if (itemTypeFilter === 'embellishment') {
        items = items.filter(item => item.embellishmentType === subcategoryFilter);
      } else if (itemTypeFilter === 'transfer') {
        items = items.filter(item => item.transferType === subcategoryFilter);
      }
    }
    
    // Apply condition filter (only for items)
    if (conditionFilter !== 'all' && (activeTab === 'destash' || activeTab === 'trade')) {
      items = items.filter(item => item.destashCondition === conditionFilter);
    }
    
    // Apply swaps only filter (only for destash tab - show items also open to swaps)
    if (swapsOnlyFilter && activeTab === 'destash') {
      items = items.filter(item => item.isOpenToSwaps === true);
    }
    
    // Apply price range filter
    if (priceRangeFilter !== 'all') {
      if (activeTab === 'destash' || activeTab === 'trade') {
        items = items.filter(item => {
          const price = item.destashPrice || 0;
          switch (priceRangeFilter) {
            case '0-10': return price < 10;
            case '10-25': return price >= 10 && price < 25;
            case '25-50': return price >= 25 && price < 50;
            case '50+': return price >= 50;
            default: return true;
          }
        });
      } else {
        filteredCreations = filteredCreations.filter(creation => {
          const price = creation.price || 0;
          switch (priceRangeFilter) {
            case '0-10': return price < 10;
            case '10-25': return price >= 10 && price < 25;
            case '25-50': return price >= 25 && price < 50;
            case '50+': return price >= 50;
            default: return true;
          }
        });
      }
    }
    
    // Apply ships-to filter (country-based). 'all' disables it.
    // An item matches when EITHER structured field says so:
    //   - destashShipsToWorldwide === true, OR
    //   - destashShipsToCountries includes the selected country
    // Items with NEITHER structured field (legacy listings) fall through
    // to the fuzzy seller.shipsTo string check so pre-migration items
    // still appear in country-filtered results.
    if (shipsToFilter !== 'all' && (activeTab === 'destash' || activeTab === 'trade')) {
      const selectedCountry = shipsToFilter;
      // Copy so we don't mutate the shared LEGACY_SHIPS_TO_KEYWORDS entry.
      const legacyKeywords = [...(LEGACY_SHIPS_TO_KEYWORDS[selectedCountry] || [])];
      const countryInfo = getCountryInfo(selectedCountry);
      if (countryInfo) legacyKeywords.push(countryInfo.name.toLowerCase());

      items = items.filter(item => {
        // Structured path (preferred)
        if (item.destashShipsToWorldwide === true) return true;
        if (item.destashShipsToCountries && item.destashShipsToCountries.length > 0) {
          return item.destashShipsToCountries.includes(selectedCountry);
        }
        // Legacy fuzzy fallback for items with neither structured field.
        const sellerShipsTo = sellerShippingInfo[item.sellerId];
        if (!sellerShipsTo) return false;
        const shipsToLower = sellerShipsTo.toLowerCase();
        // Match seller's free-text shipsTo against country keywords OR
        // any "worldwide"-family keyword (worldwide sellers cover all).
        if (LEGACY_WORLDWIDE_KEYWORDS.some(k => shipsToLower.includes(k))) return true;
        return legacyKeywords.some(k => shipsToLower.includes(k));
      });
    }
    
    // Apply sorting
    if (activeTab === 'destash' || activeTab === 'trade') {
      items = [...items].sort((a, b) => {
        switch (sortBy) {
          case 'recent':
            const dateA = a.dateAdded ? new Date(a.dateAdded).getTime() : 0;
            const dateB = b.dateAdded ? new Date(b.dateAdded).getTime() : 0;
            return dateB - dateA; // Newest first
          case 'price-low':
            return (a.destashPrice || 0) - (b.destashPrice || 0);
          case 'price-high':
            return (b.destashPrice || 0) - (a.destashPrice || 0);
          case 'name-az':
            return (a.name || '').localeCompare(b.name || '');
          default:
            return 0;
        }
      });
    } else {
      filteredCreations = [...filteredCreations].sort((a, b) => {
        switch (sortBy) {
          case 'recent':
            const dateA = a.createdAt?.toMillis?.() || 0;
            const dateB = b.createdAt?.toMillis?.() || 0;
            return dateB - dateA; // Newest first
          case 'price-low':
            return (a.price || 0) - (b.price || 0);
          case 'price-high':
            return (b.price || 0) - (a.price || 0);
          case 'name-az':
            return (a.title || '').localeCompare(b.title || '');
          default:
            return 0;
        }
      });
    }
    
    return { items, creations: filteredCreations };
  };

  const { items: filteredItems, creations: filteredCreations } = getFilteredContent();
  
  // Get count for current tab
  const getCurrentCount = () => {
    if (activeTab === 'destash') return filteredItems.length;
    if (activeTab === 'trade') return filteredItems.length;
    return filteredCreations.length;
  };

  // Get unique item types for filter dropdown (kept for future use)
  // const itemTypes = [...new Set([...destashItems, ...tradeItems].map(item => item.itemType))].filter(Boolean);

  // Tab counts
  const tabCounts: Record<TabType, number> = {
    destash: destashItems.length,
    trade: tradeItems.length,
    pressons: creations.filter(c => c.marketplaceCategory === 'pressons').length,
    jewelry: creations.filter(c => c.marketplaceCategory === 'jewelry').length,
    other: creations.filter(c => c.marketplaceCategory === 'other').length,
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header - GLASSMORPHIC */}
        <PageHeader
          icon={ShoppingBag}
          title="Marketplace"
          subtitle="Browse items and creations from the community"
          className="mb-6"
          actions={
            <>
              <span className="text-sm text-glass-muted font-body">
                {getCurrentCount()} {getCurrentCount() === 1 ? 'item' : 'items'}
              </span>
              {currentUserId && (
                <>
                  <Link
                    to="/my-wants"
                    className="p-2 text-glass-muted hover:text-amber-500 transition-default"
                    title="Wishlist/Watchlist"
                  >
                    <Star className="icon-md text-current" />
                  </Link>
                  <Link
                    to="/marketplace/favorites"
                    className="p-2 text-glass-muted hover:text-red-500 transition-default"
                    title="Favorite Sellers"
                  >
                    <Heart className="icon-md text-current" />
                  </Link>
                </>
              )}
              <button
                onClick={fetchMarketplaceData}
                className="p-2 text-glass-muted hover:text-glass transition-default"
                title="Refresh"
              >
                <RefreshCw className={`icon-md text-current ${loading ? 'animate-spin' : ''}`} />
              </button>
            </>
          }
        >
          {/* Seller Filter Banner */}
          {sellerFilter && (
            <div className="mt-4 flex items-center gap-3 p-3 bg-slate-700/20 border border-slate-500 rounded-xl text-glass-secondary">
              <User className="icon-md text-current flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-glass font-body">
                  Showing items from <span className="font-bold">{sellerFilter.displayName}</span>
                  <span className="text-glass-secondary ml-1">@{sellerFilter.username}</span>
                </p>
              </div>
              <button
                onClick={clearSellerFilter}
                className="p-1.5 text-glass-secondary hover:text-glass hover:bg-slate-700 rounded-full transition-default"
                title="Clear filter"
              >
                <X className="icon-sm text-current" />
              </button>
            </div>
          )}
        </PageHeader>

        {/* Sell-your-own hint — testers reported not knowing how to list
            an item. Points them to the collection Add flow with a short
            note about the destash/trade toggles. Only shown when NOT
            filtered by a specific seller (that view is a browse mode). */}
        {!sellerFilter && (
          <Link
            to="/collection?add=1"
            className="mb-4 flex items-center gap-3 rounded-xl border border-amber-400/60 bg-amber-500/10 p-3 hover:bg-amber-500/15 transition-default"
          >
            <ShoppingBag className="w-5 h-5 text-amber-300 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-100 font-body">
                Have items to sell or swap?
              </p>
              <p className="text-xs text-amber-100/80 font-body">
                Add an item to your collection, then flip <strong>For Destash</strong> or <strong>Open to swaps</strong>.
              </p>
            </div>
            <span className="text-amber-300 text-lg">›</span>
          </Link>
        )}

        {/* Tabs & Filters */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  // Reset category-specific filters when changing tabs
                  setItemTypeFilter('all');
                  setSubcategoryFilter('all');
                  setConditionFilter('all');
                  setSwapsOnlyFilter(false);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium font-body transition-all ${
                  activeTab === tab.id
                    ? 'filter-active shadow-md'
                    : 'filter-inactive'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tabCounts[tab.id] > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === tab.id
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-700 text-white'
                  }`}>
                    {tabCounts[tab.id]}
                  </span>
                )}
              </button>
            ))}
          </div>
          
          {/* Search & Filters */}
          <div className="mt-4 space-y-3">
            {/* Row 1: Search + Sort */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-muted" />
                <input
                  type="text"
                  placeholder={activeTab === 'destash' || activeTab === 'trade'
                    ? "Search by name, brand, or color..."
                    : "Search creations..."
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-800/60 border-0 rounded-xl focus:ring-2 focus:ring-slate-500 text-glass placeholder:text-glass-muted font-body"
                />
              </div>
              
              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 font-body text-light-primary min-w-[180px]"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            {/* Row 2: Category Filters (for destash/trade tabs) */}
            {(activeTab === 'destash' || activeTab === 'trade') && (
              <div className="flex flex-wrap gap-3">
                {/* Category */}
                <select
                  value={itemTypeFilter}
                  onChange={(e) => {
                    setItemTypeFilter(e.target.value);
                    setSubcategoryFilter('all'); // Reset subcategory when category changes
                  }}
                  className="px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 font-body text-light-primary"
                >
                  {ITEM_CATEGORIES.map(cat => (
                    <option key={cat.key} value={cat.key}>{cat.label}</option>
                  ))}
                </select>
                
                {/* Subcategory - only show if main category has subcategories */}
                {SUBCATEGORIES[itemTypeFilter] && (
                  <select
                    value={subcategoryFilter}
                    onChange={(e) => setSubcategoryFilter(e.target.value)}
                    className="px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 font-body text-light-primary"
                  >
                    {SUBCATEGORIES[itemTypeFilter].map(sub => (
                      <option key={sub.key} value={sub.key}>{sub.label}</option>
                    ))}
                  </select>
                )}
                
                {/* Condition */}
                <select
                  value={conditionFilter}
                  onChange={(e) => setConditionFilter(e.target.value)}
                  className="px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 font-body text-light-primary"
                >
                  {CONDITION_OPTIONS.map(opt => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>

                {/* Price Range */}
                <select
                  value={priceRangeFilter}
                  onChange={(e) => setPriceRangeFilter(e.target.value)}
                  className="px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 font-body text-light-primary"
                >
                  {PRICE_RANGE_OPTIONS.map(opt => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>

                {/* Ships To - country-based, grouped by region */}
                <select
                  value={shipsToFilter}
                  onChange={(e) => {
                    // Once the user picks a country manually, lock in the
                    // initialized flag so a later profile fetch doesn't
                    // overwrite their choice.
                    setShipsToFilter(e.target.value);
                    setShipsToFilterInitialized(true);
                  }}
                  className="px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 font-body text-light-primary"
                  aria-label="Ships to country"
                >
                  <option value="all">Ships To: All countries</option>
                  {SHIPS_TO_REGION_ORDER.map(region => {
                    const countries = SHIPS_TO_COUNTRY_GROUPS[region];
                    if (!countries || countries.length === 0) return null;
                    return (
                      <optgroup key={region} label={region}>
                        {countries.map(c => (
                          <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
                
                {/* Open to Swaps Toggle - only on destash tab */}
                {activeTab === 'destash' && (
                  <button
                    onClick={() => setSwapsOnlyFilter(!swapsOnlyFilter)}
                    className={`px-4 py-2.5 rounded-xl font-body transition-all flex items-center gap-2 ${
                      swapsOnlyFilter
                        ? `${CategoryColors.polish.bg} text-white`
                        : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                    }`}
                  >
                    <Repeat className="icon-sm" />
                    Open to Trades
                  </button>
                )}
              </div>
            )}
            
            {/* Row 2: Price & Sort for creations tabs */}
            {(activeTab === 'pressons' || activeTab === 'jewelry' || activeTab === 'other') && (
              <div className="flex flex-wrap gap-3">
                {/* Price Range */}
                <select
                  value={priceRangeFilter}
                  onChange={(e) => setPriceRangeFilter(e.target.value)}
                  className="px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 font-body text-light-primary"
                >
                  {PRICE_RANGE_OPTIONS.map(opt => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Active Filters Summary / Clear All */}
            {(itemTypeFilter !== 'all' || subcategoryFilter !== 'all' || conditionFilter !== 'all' || priceRangeFilter !== 'all' || shipsToFilter !== 'all' || swapsOnlyFilter || searchTerm) && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-glass-muted">Active filters:</span>
                {searchTerm && (
                  <span className="text-xs px-2 py-1 bg-slate-700 rounded-full text-glass">
                    Search: "{searchTerm}"
                  </span>
                )}
                {itemTypeFilter !== 'all' && (
                  <span className="text-xs px-2 py-1 bg-slate-700 rounded-full text-glass">
                    {ITEM_CATEGORIES.find(c => c.key === itemTypeFilter)?.label}
                  </span>
                )}
                {subcategoryFilter !== 'all' && (
                  <span className="text-xs px-2 py-1 bg-slate-700 rounded-full text-glass">
                    {subcategoryFilter}
                  </span>
                )}
                {conditionFilter !== 'all' && (
                  <span className="text-xs px-2 py-1 bg-slate-700 rounded-full text-glass">
                    {conditionFilter}
                  </span>
                )}
                {priceRangeFilter !== 'all' && (
                  <span className="text-xs px-2 py-1 bg-slate-700 rounded-full text-glass">
                    {PRICE_RANGE_OPTIONS.find(p => p.key === priceRangeFilter)?.label}
                  </span>
                )}
                {shipsToFilter !== 'all' && (
                  <span className="text-xs px-2 py-1 bg-slate-700 rounded-full text-glass">
                    Ships to: {getCountryInfo(shipsToFilter)?.name || shipsToFilter}
                  </span>
                )}
                {swapsOnlyFilter && (
                  <span className={`text-xs px-2 py-1 ${CategoryColors.polish.bg} rounded-full text-white`}>
                    Open to Swaps
                  </span>
                )}
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setItemTypeFilter('all');
                    setSubcategoryFilter('all');
                    setConditionFilter('all');
                    setPriceRangeFilter('all');
                    setShipsToFilter('all');
                    setSwapsOnlyFilter(false);
                  }}
                  className="text-xs px-2 py-1 text-red-400 hover:text-red-300 underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Error State */}
        {error && (
          <div className="alert-glass-red rounded-2xl mb-6">
            <p className="text-red-300 font-body">{error}</p>
          </div>
        )}
        
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <RefreshCw className="icon-2xl text-glass animate-spin mx-auto mb-4" />
              <p className="text-glass-secondary font-body">Loading marketplace...</p>
            </div>
          </div>
        )}
        
        {/* Empty State */}
        {!loading && !error && getCurrentCount() === 0 && (
          <div className="glass-card p-12 text-center">
            <div className="text-6xl mb-4">
              {activeTab === 'destash' && '🏷️'}
              {activeTab === 'trade' && '🔄'}
              {activeTab === 'pressons' && '💅'}
              {activeTab === 'jewelry' && '💍'}
              {activeTab === 'other' && '✨'}
            </div>
            <h2 className="text-2xl text-glass mb-2 font-heading">
              {searchTerm 
                ? 'No items match your search'
                : `No ${TABS.find(t => t.id === activeTab)?.label.toLowerCase()} listings yet`
              }
            </h2>
            <p className="text-glass-secondary font-body">
              {searchTerm
                ? 'Try adjusting your search'
                : 'Check back later for new listings!'
              }
            </p>
          </div>
        )}
        
        {/* Items Grid (for destash/trade tabs) */}
        {!loading && !error && (activeTab === 'destash' || activeTab === 'trade') && filteredItems.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item, index) => (
              <MarketplaceItemCard
                key={`${item.sellerId}-${item.id}`}
                item={item}
                currentUserId={currentUserId}
                onCopySuccess={() => {}}
                onClick={() => {
                  setSelectedItem(item);
                  setSelectedItemIndex(index);
                }}
                isWatching={watchingItems[`${item.id}-${item.sellerId}`]}
                onWatchChange={(watching) => handleItemWatchChange(item.id, item.sellerId, watching)}
              />
            ))}
          </div>
        )}

        {/* Creations Grid (for pressons/jewelry/other tabs) */}
        {!loading && !error && (activeTab === 'pressons' || activeTab === 'jewelry' || activeTab === 'other') && filteredCreations.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCreations.map((creation, index) => (
              <MarketplaceCreationCard
                key={`${creation.sellerId}-${creation.id}`}
                creation={creation}
                currentUserId={currentUserId}
                onClick={() => {
                  setSelectedCreation(creation);
                  setSelectedCreationIndex(index);
                }}
                isWatching={watchingCreations[`${creation.id}-${creation.sellerId}`]}
                onWatchChange={(watching) => handleCreationWatchChange(creation.id, creation.sellerId, watching)}
              />
            ))}
          </div>
        )}

        {/* Item Detail Modal */}
        {selectedItem && (
          <MarketplaceItemDetailModal
            item={selectedItem}
            items={filteredItems}
            currentIndex={selectedItemIndex}
            currentUserId={currentUserId}
            onClose={() => {
              setSelectedItem(null);
              setSelectedItemIndex(-1);
            }}
            onNavigate={(index) => {
              setSelectedItem(filteredItems[index]);
              setSelectedItemIndex(index);
            }}
            onCopySuccess={() => {}}
            onEdit={handleEditListing}
            isWatching={watchingItems[`${selectedItem.id}-${selectedItem.sellerId}`]}
            onWatchChange={(watching) => handleItemWatchChange(selectedItem.id, selectedItem.sellerId, watching)}
          />
        )}

        {/* Creation Detail Modal */}
        {selectedCreation && (
          <MarketplaceCreationDetailModal
            creation={selectedCreation}
            creations={filteredCreations}
            currentIndex={selectedCreationIndex}
            currentUserId={currentUserId}
            onClose={() => {
              setSelectedCreation(null);
              setSelectedCreationIndex(-1);
            }}
            onNavigate={(index) => {
              setSelectedCreation(filteredCreations[index]);
              setSelectedCreationIndex(index);
            }}
            isWatching={watchingCreations[`${selectedCreation.id}-${selectedCreation.sellerId}`]}
            onWatchChange={(watching) => handleCreationWatchChange(selectedCreation.id, selectedCreation.sellerId, watching)}
          />
        )}
      </div>
    </div>
  );
};

export default Marketplace;
